import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Paper from '@material-ui/core/Paper';
import { Title, useNotify } from 'react-admin';
import useFirebaseToken from '../../bitComponents/utils/hooks/useFirebaseToken';
import labels from '../../labels';
import { syncCms, syncCmsFromBody } from './api';

const networkErrorMessage = (e) =>
  e &&
  (e.message === 'Failed to fetch' ||
    e.name === 'TypeError' ||
    String(e.message || '').includes('NetworkError'));

const formatCounts = ({ upserted, deleted }) =>
  `עודכנו: ${upserted ?? 0}, נמחקו: ${deleted ?? 0}`;

const SyncResultSummary = ({ result }) => {
  const { total, content, tags, articles, makes, skippedContentTypes } =
    result;

  return (
    <Box component="ul" style={{ margin: 0, paddingRight: 20 }}>
      <li>סה״כ פריטים מ-Umbraco: {total ?? 0}</li>
      <li>תוכן: {formatCounts(content || {})}</li>
      <li>תגיות: {formatCounts(tags || {})}</li>
      <li>מאמרים: {formatCounts(articles || {})}</li>
      <li>יצרנים: {formatCounts(makes || {})}</li>
      {skippedContentTypes?.length > 0 && (
        <li>סוגי תוכן שדולגו: {skippedContentTypes.join(', ')}</li>
      )}
    </Box>
  );
};

const ValidationIssues = ({ issues }) => {
  if (!issues || issues.length === 0) return null;
  return (
    <Box component="ul" style={{ margin: '8px 0 0', paddingRight: 20 }}>
      {issues.slice(0, 20).map((issue, index) => {
        const path =
          Array.isArray(issue.path) && issue.path.length > 0
            ? issue.path.join('.')
            : '';
        return (
          <li key={`${path}-${index}`}>
            {path ? `${path}: ` : ''}
            {issue.message || 'שגיאת ולידציה'}
          </li>
        );
      })}
      {issues.length > 20 && <li>…ועוד {issues.length - 20} שגיאות</li>}
    </Box>
  );
};

/**
 * Normalize pasted/uploaded JSON into `{ items: [...] }` for the API.
 * @param {unknown} value
 * @returns {{ items: object[] }}
 */
function normalizeSyncFromBodyPayload(value) {
  if (Array.isArray(value)) {
    return { items: value };
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.Data?.items)) {
      return { items: value.Data.items };
    }
    if (Array.isArray(value.items)) {
      return { items: value.items };
    }
  }
  throw new Error('JSON חייב להכיל items');
}

const CmsList = () => {
  const notify = useNotify();
  const { token } = useFirebaseToken();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [jsonText, setJsonText] = useState('');
  const [validationIssues, setValidationIssues] = useState(null);

  const resetOutcome = () => {
    setStatus(null);
    setSyncResult(null);
    setValidationIssues(null);
  };

  const handleSync = async () => {
    if (!token) {
      notify('יש להמתין רגע ולנסות שוב.', 'warning');
      return;
    }
    setLoading(true);
    resetOutcome();
    try {
      const result = await syncCms(token);
      setSyncResult(result);
      setStatus({
        type: 'success',
        message: 'סנכרון CMS הושלם בהצלחה.'
      });
      notify('סנכרון CMS הושלם בהצלחה.', 'success');
    } catch (e) {
      const msg = networkErrorMessage(e)
        ? 'לא ניתן להשלים את הפעולה כרגע. נסו שוב מאוחר יותר.'
        : e.message || 'הפעולה נכשלה.';
      setStatus({ type: 'error', message: msg });
      notify(msg, 'warning');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setJsonText(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      notify('לא ניתן לקרוא את הקובץ.', 'warning');
    };
    reader.readAsText(file);
    // Allow re-selecting the same file
    event.target.value = '';
  };

  const handleSyncFromBody = async () => {
    if (!token) {
      notify('יש להמתין רגע ולנסות שוב.', 'warning');
      return;
    }
    const trimmed = jsonText.trim();
    if (!trimmed) {
      notify('יש להדביק או להעלות JSON.', 'warning');
      return;
    }

    setLoading(true);
    resetOutcome();
    try {
      let parsed;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        throw new Error('JSON לא תקין');
      }

      const payload = normalizeSyncFromBodyPayload(parsed);
      const result = await syncCmsFromBody(token, payload);
      setSyncResult(result);
      setStatus({
        type: 'success',
        message: 'סנכרון מ-JSON הושלם בהצלחה.'
      });
      notify('סנכרון מ-JSON הושלם בהצלחה.', 'success');
    } catch (e) {
      const msg = networkErrorMessage(e)
        ? 'לא ניתן להשלים את הפעולה כרגע. נסו שוב מאוחר יותר.'
        : e.message || 'הפעולה נכשלה.';
      setValidationIssues(e.issues || null);
      setStatus({ type: 'error', message: msg });
      notify(msg, 'warning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ paddingBottom: 100 }}>
      <Title title={labels.cms} />
      <Paper style={{ marginTop: 16 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              סנכרון תוכן CMS
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              סנכרון תוכן מ-Umbraco ל-PostgreSQL. הפעולה שולפת את כל פריטי
              ה-CMS מ-Umbraco ומעדכנת את מסד הנתונים.
            </Typography>
            <Box
              marginTop={2}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={loading || !token}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={18} color="inherit" />
                    מסנכרן…
                  </Box>
                ) : (
                  'סנכרון CMS'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>

      <Paper style={{ marginTop: 16 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              סנכרון מ-JSON
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              הדביקו או העלו JSON של פריטי Umbraco (מערך items, או מעטפת
              Mimun עם Data.items) וסנכרנו ישירות ל-PostgreSQL ללא קריאה
              ל-Umbraco.
            </Typography>
            <TextField
              label="JSON"
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              disabled={loading}
              dir="ltr"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Box
              marginTop={2}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 12 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                color="primary"
                disabled={loading}
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
              >
                העלאת קובץ JSON
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSyncFromBody}
                disabled={loading || !token || !jsonText.trim()}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={18} color="inherit" />
                    מסנכרן…
                  </Box>
                ) : (
                  'סנכרון מ-JSON'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>

      {status && (
        <Box marginTop={2}>
          <Alert severity={status.type}>
            <Typography variant="body2" gutterBottom>
              {status.message}
            </Typography>
            {syncResult && <SyncResultSummary result={syncResult} />}
            {validationIssues && (
              <ValidationIssues issues={validationIssues} />
            )}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CmsList;
